/**
 * Unified place for API requests to happen, share intervals, reduce duplication
 */

import { default as DataLoader } from 'dataloader';
import differenceInSeconds from 'date-fns/differenceInSeconds';

import flow from 'lodash/flow';
import groupBy from 'lodash/groupBy';
import over from 'lodash/over';
import pickBy from 'lodash/pickBy';
import sortBy from 'lodash/sortBy';

// Types
import { RefreshRate } from '../types';

// Config
import { API_BASE, GLOBAL_POLLING_RATE } from '../config';

// Utils
import { fetchMockData } from './processing';
import { rateForSize } from './util';

// Single request with no expectation of polling
interface IBMRequest {
  // /v1/data/basstrakk-leaderboard/1225
  uri: string;
  // Function to return the Request object to
  callback: (r: any) => void;
}
// Polling requests require a rate
interface IBMRequestSubscribe extends IBMRequest {
  // milliseconds, ie [5000, 5000]
  rate: RefreshRate;
}

// The queue is filtered down into this shape
interface IFilteredQueue {
  [uri: string]: IFilteredQueueValue[];
}
interface IFilteredQueueValue {
  rate: RefreshRate;
  callback: (r: any) => void;
}

/**
 * If you would like to use mock data, add REACT_APP_API_MOCK='stage' to your
 * "npm run" command. This will tell all functions that request data to look
 * for a json file in client/public/
 *
 * @see fetchMockData in client/src/util/processing.tsx
 */

/**
 * @Bass-Blocks-Integration
 * Hard coding env variables for now.
 * Checking for `process.env` breaks browser js. The global process object only exists in server side Node environments.
 */
// const useMockData = process.env.REACT_APP_API_MOCK === 'files';
const useMockData = false;

// Simple array of objects representing the api, a refresh rate, and a callback
export const queue: IBMRequestSubscribe[] = [];
export const addToQueue = (item: IBMRequestSubscribe) => queue.push(item);
export const clearQueue = () => (queue.length = 0);

// Timer for global tick
// If during testing (debug) you find the refresh (pollinterval) to be too fast you
// can temporarily increase this value
let pollInterval = 0;

/**
 * Sort first by unique uri, then lowest -> highest rate (for window size).
 *
 * Starting shape:
 * [
 *   {uri: '/data/bar', callback: data => doStuff(data), rate: [30000, 10000]},
 *   {uri: '/data/baz', callback: data => doStuff(data), rate: [15000, 10000]},
 *   {uri: '/data/foo', callback: data => doStuff(data), rate: [5000, 10000]},
 *   {uri: '/data/foo', callback: data => doStuff(data), rate: [30000, 10000]},
 * ]
 * @param requests
 */
export const sortByUriRate = (requests: IBMRequestSubscribe[]) =>
  sortBy(requests, [
    'uri',
    ({ rate }: IBMRequestSubscribe) => rateForSize(rate),
  ]);

/**
 * Group all requests by uri, makes an object like:
 * {
 *   '/data/bar': [{callback: data => doStuff(data), rate: [30000, 10000]},
 *   '/data/baz': [{callback: data => doStuff(data), rate: [15000, 10000]},
 *   '/data/foo': [
 *     {callback: data => doStuff(data), rate: [5000, 10000]},
 *     {callback: data => doStuff(data), rate: [30000, 10000]},
 *   ],
 * }
 * @param requests
 */
export const groupByUri = (requests: IBMRequestSubscribe[]): IFilteredQueue =>
  groupBy(requests, 'uri');

/**
 * Filter only the keys that should update on THIS tick. At 10 seconds,
 * note that /data/baz, /data/bar is missing because their LOWEST rate
 * (30, 15 seconds, respectively) does not divide into elapsed time (10
 * seconds) evenly:
 * {
 *    '/data/foo': [
 *      {callback: data => doStuff(data), rate: [5000, 10000]},
 *      {callback: data => doStuff(data), rate: [30000, 10000]},
 *    ],
 *  }
 * @param seconds
 */
export const pickByTick = (seconds: number) => (requests: IFilteredQueue) =>
  pickBy(requests, requestsPerUri => {
    // First rate will always be lowest due to sorting in first step
    const lowestRateInSeconds = rateForSize(requestsPerUri[0].rate) / 1000;
    // Only update if rate is evenly divisible by current elapsed time
    return seconds !== 0 && seconds % lowestRateInSeconds === 0;
  });

/**
 * Return a PROCESSOR function that is ready to receive a queue collection
 * @param seconds - The only arg that changes is time elapsed
 * @returns {Function} - Function ready to take in the queue for processing
 */
export const makeQueueProcessor = (
  seconds: number
): ((q: IBMRequestSubscribe[]) => IFilteredQueue) =>
  flow([sortByUriRate, groupByUri, pickByTick(seconds)]);

/**
 * Fetch each unique URI and pass its data back to each registered callback.
 *
 * Expect an object whose keys are the URI to request, and whose values are the
 * callbacks to run with the data. Object looks like:
 *   {
 *     '/data/foo': {
 *       requests: [
 *         { callback: data => doStuff(data), rate: [5000, 10000] },
 *         { callback: data => doStuff(data), rate: [30000, 10000] },
 *       ],
 *     },
 *     '/data/bar': { requests: [{ callback: data => blah}, rate: [5000, 5000]}
 *   }
 */
export const callRequestCallbacks = (filteredQueue: IFilteredQueue): void =>
  Object.entries(filteredQueue).forEach(async ([uri, requests]) => {
    // Check to see if we should use mock data or just return from the api
    const data =
      (useMockData ? await fetchMockData(uri) : null) ||
      (await requestLoader.clear(uri).load(uri));
    // Call every callback with the provided data
    over(requests.map(r => r.callback))(data);
  });

/**
 * Run the callbacks that need to be called at this period of time
 */
const processQueue = (seconds: number, requestQueue: IBMRequestSubscribe[]) => {
  // Make a function that is ready to process the queue
  const queueProcessor = makeQueueProcessor(seconds);
  // Filter down the overall queue to something to be run on THIS tick
  const reducedQueue = queueProcessor(requestQueue);
  // Call all the requests and send the results to their respective callbacks
  callRequestCallbacks(reducedQueue);
};

/**
 * Set polling function to provide data to all callbacks.
 *
 * Ensures that all requests in the queue do not duplicate API requests and
 * run at the refresh rate of the lowest unique request. Allows arbitrary
 * components on a page to make requests willy-nilly in a safe way.
 */
export const startPolling = () => {
  // The base reference point all ticks refer back to
  const pollStartTime = new Date();

  return window.setInterval(() => {
    // Time since start, rounded for nice math
    const timeElapsedInSeconds = differenceInSeconds(new Date(), pollStartTime);

    // Process the overall queue
    processQueue(timeElapsedInSeconds, queue);
  }, GLOBAL_POLLING_RATE);
};

/**
 * Raw fetch request to endpoint. Returns Promise with data.
 */
export const makeRequests = (uris: readonly string[]): Promise<any> =>
  Promise.all(
    uris.map(async uri => {
      const fullUri = uri.startsWith('/wp-json/') || uri.startsWith('http') ? uri : `${API_BASE}${uri}`;
      try {
        const res = await fetch(fullUri);
        if (res.ok) {
          return await res.json();
        }
      } catch (err) {
        // What do we do when fetch fails?
        // console.log(err); // tslint:disable-line:no-console
      }
    })
  );

/**
 * DataLoader memoization
 */
export const requestLoader = new DataLoader<string, any>(makeRequests);

/**
 * Make single request with no subscription requirement
 */
export const get = async (r: IBMRequest) => {
  // Check to see if we should use mock data or api data
  const data =
    (useMockData ? await fetchMockData(r.uri) : null) ||
    (await requestLoader.clear(r.uri).load(r.uri));
  r.callback(data);
};

/**
 * Add to array
 */
export const subscribe = async (r: IBMRequestSubscribe) => {
  // Just call get, don't wait to add to queue though
  get(r).then();
  // Add to queue for future runs
  addToQueue(r);
  // Start loop if not already started
  if (!pollInterval) {
    pollInterval = startPolling();
  }
};

export default subscribe;
